import {AsyncPipe} from '@angular/common';
import {AfterViewInit, Component, DoCheck, OnInit, ViewChild} from '@angular/core';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatSidenav, MatSidenavContainer, MatSidenavContent} from '@angular/material/sidenav';
import {MatToolbar, MatToolbarRow} from '@angular/material/toolbar';
import {MatTooltip} from '@angular/material/tooltip';
import {Title} from '@angular/platform-browser';
import {Router, RouterLink} from '@angular/router';

import {Client, ClientService} from '@app/admin/client.service';
import {AssetListComponent} from '@app/asset/asset-list/asset-list.component';
import {AssetService} from '@app/asset/asset.service';
import {FilterPanelButtonComponent} from '@app/assetfilter/filter-panel-button/filter-panel-button.component';
import {FiltersSheetButtonComponent} from '@app/assetfilter/filters-sheet-button/filters-sheet-button.component';
import {FiltersComponent} from '@app/assetfilter/filters/filters.component';
import {SearchComponent} from '@app/assetfilter/search/search.component';
import {InspectorService} from '@app/inspector/inspector.service';
import {
    InspectorComponent,
    InspectorComponent as InspectorComponent_1
} from '@app/inspector/inspector/inspector.component';
import {InviteUsersButtonComponent} from '@app/invitation/invite-users-button/invite-users-button.component';
import {AddReviewButtonComponent} from '@app/review/add-review-button/add-review-button.component';
import {SelectionToolbarComponent} from '@app/selection/selection-toolbar/selection-toolbar.component';
import {SelectionService} from '@app/selection/selection.service';
import {SetupComponent} from '@app/setup/setup/setup.component';
import {ManagedComponent} from '@app/shared/managed.component';
import {SetupState, SiteConfig, SiteConfigService} from '@app/shared/site-config.service';
import {StorageService} from '@app/shared/storage.service';
import {SiteMenuComponent} from '@app/site-menu/site-menu/site-menu.component';
import {SiteToolbarComponent} from '@app/site-menu/site-toolbar/site-toolbar.component';
import {StateService} from '@app/state/state.service';
import {StreamListComponent} from '@app/stream/stream-list/stream-list.component';
import {StreamMenuComponent} from '@app/stream/stream-menu/stream-menu.component';
import {StreamService} from '@app/stream/stream.service';
import {UploadButtonComponent} from '@app/upload/upload-button/upload-button.component';
import {UploadDropComponent} from '@app/upload/upload-drop/upload-drop.component';
import {ViewerComponent} from '@app/viewer/viewer/viewer.component';
import {WorkspaceListComponent} from '@app/workspace/workspace-list/workspace-list.component';
import {NgScrollbar} from 'ngx-scrollbar';
import {combineLatest, Observable} from 'rxjs';

import {distinctUntilChanged, filter, map} from 'rxjs/operators';

@Component({
    selector: 'frog-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss'],
    imports: [
        MatSidenavContainer,
        MatSidenav,
        NgScrollbar,
        WorkspaceListComponent,
        StreamListComponent,
        SiteToolbarComponent,
        MatIconButton,
        MatIcon,
        InspectorComponent_1,
        MatSidenavContent,
        MatToolbar,
        MatToolbarRow,
        SetupComponent,
        ViewerComponent,
        UploadDropComponent,
        SelectionToolbarComponent,
        RouterLink,
        UploadButtonComponent,
        MatTooltip,
        SearchComponent,
        FiltersSheetButtonComponent,
        FilterPanelButtonComponent,
        InviteUsersButtonComponent,
        SiteMenuComponent,
        StreamMenuComponent,
        FiltersComponent,
        AssetListComponent,
        AsyncPipe,
        AddReviewButtonComponent,
    ],
})
export class MainComponent extends ManagedComponent implements OnInit, AfterViewInit, DoCheck {
    @ViewChild('sidenav') sidenav: MatSidenav;

    public selection$: Observable<string[]>;
    public viewingAsset$: Observable<string>;
    public siteConfig$: Observable<SiteConfig>;
    public client$: Observable<Client>;
    public SetupState = SetupState;
    private resetData: boolean;

    constructor(
        private inspectorService: InspectorService,
        private streamService: StreamService,
        private assetService: AssetService,
        private stateService: StateService,
        private siteConfigService: SiteConfigService,
        private clientService: ClientService,
        private selectionService: SelectionService,
        private storageService: StorageService,
        private titleService: Title,
        private matBottomSheet: MatBottomSheet,
        private router: Router,
    ) {
        super();
        this.resetData = false;
        this.storageService.pop('scrollPosition');
    }

    ngOnInit(): void {
        this.selection$ = this.stateService.state.pipe(
            map((state) => state.selection),
            distinctUntilChanged(),
        );
        this.viewingAsset$ = this.stateService.state.pipe(
            map((state) => state.viewingAsset),
            distinctUntilChanged(),
        );

        this.siteConfig$ = this.siteConfigService.siteConfig.pipe(
            filter((config) => Boolean(config.data)),
        );
        this.client$ = this.clientService.get();

        const streamObservable = this.streamService.stream.pipe(
            filter((stream) => Boolean(stream)),
        );

        let sub = combineLatest([streamObservable, this.siteConfig$]).subscribe(
            ([stream, siteConfig]) => {
                this.titleService.setTitle(`${siteConfig.data['name']} :: ${stream.name}`);
            },
        );
        this.subscriptions.push(sub);

        sub = this.router.events.subscribe(() => {
            this.resetData = true;
        });
        this.subscriptions.push(sub);
    }

    ngDoCheck() {
        if (!this.resetData) {
            return;
        }
        this.resetData = false;
        const scrollPosition = this.storageService.get('scrollPosition');
        if (scrollPosition !== null) {
            document.getElementsByTagName('mat-sidenav-content')[0].scrollTo(0, scrollPosition);
        }
    }

    ngAfterViewInit() {
        let sub = this.inspectorService.asset
            .pipe
            // filter(asset => asset !== null),
            // distinctUntilChanged()
            ()
            .subscribe((asset) => {
                if (asset === null) {
                    this.sidenav.close();
                } else {
                    if (window.outerWidth < 600) {
                        this.matBottomSheet.open(InspectorComponent, { data: asset });
                    } else {
                        this.sidenav.open('program');
                    }
                }
            });
        this.subscriptions.push(sub);

        sub = this.sidenav.closedStart.subscribe(() => this.inspectorService.inspect(null));
        this.subscriptions.push(sub);
    }

    selectMode() {
        this.selectionService.setSelectMode(true);
    }

    toggleWorkspaces(): void {
        this.sidenav.open('program');
    }
}
